/**
 * @critical PARSING DE PERGUNTAS - FUNÇÃO CRÍTICA
 * @description Utilitários CRÍTICOS para extração de perguntas de mensagens
 *
 * @purpose EVITA OVERRIDE: Estas funções são ESSENCIAIS para o funcionamento do painel de perguntas.
 * Se modificadas incorretamente, perguntas podem não aparecer ou aparecer incorretamente.
 *
 * @dependencies CRÍTICAS:
 * - Chat8PerguntasPanel - Consome parseQuestionsFromMessage
 * - WebSocket messages - Fonte das mensagens para parsing
 * - Regex patterns - Padrões para extração de perguntas
 *
 * @warning NÃO REMOVER: parseQuestionsFromMessage é OBRIGATÓRIA
 * @warning NÃO MODIFICAR: Regex patterns são CRÍTICOS
 * @warning NÃO SUBSTITUIR: Outras funções não podem substituir esta funcionalidade
 */

import { logger } from "../logger";

/**
 * Extrai a parte introdutória da mensagem (antes das perguntas)
 * @param message - Conteúdo da mensagem
 * @param questionStartIndex - Índice onde começam as perguntas
 * @returns Texto introdutório
 */
function extractIntro(message: string, questionStartIndex: number): string {
  if (questionStartIndex <= 0) return "";

  const intro = message.substring(0, questionStartIndex).trim();
  return intro.length > 0 ? intro : "";
}

/**
 * Extrai a parte final da mensagem (após as perguntas)
 * @param message - Conteúdo da mensagem
 * @param questionEndIndex - Índice onde terminam as perguntas
 * @returns Texto final
 */
function extractOutro(message: string, questionEndIndex: number): string {
  if (questionEndIndex >= message.length) return "";

  const outro = message.substring(questionEndIndex).trim();
  return outro.length > 0 ? outro : "";
}

/**
 * Encontra o índice onde começam as perguntas
 * @param message - Conteúdo da mensagem
 * @returns Índice do início das perguntas
 */
function findQuestionStartIndex(message: string): number {
  const questionPatterns = [
    /^\s*(\d+\.\s*)/m, // 1.
    /^\s*[-•]\s*/m, // - ou •
    /^\s*(\w+\.\s*)/m, // a., b., etc.
    /^\s*(\d+\)\s*)/m, // 1)
    /^\s*\(\d+\)\s*/m, // (1)
  ];

  for (const pattern of questionPatterns) {
    const match = pattern.exec(message);
    if (match) {
      return match.index || 0;
    }
  }

  // Se não encontrou padrão estruturado, procurar por frases interrogativas
  const sentences = message.split(/[.!?]+/);
  for (let i = 0; i < sentences.length; i++) {
    const sentence = sentences[i].trim();
    if (
      sentence.includes("?") ||
      sentence.toLowerCase().startsWith("qual") ||
      sentence.toLowerCase().startsWith("quais") ||
      sentence.toLowerCase().startsWith("como") ||
      sentence.toLowerCase().startsWith("quando") ||
      sentence.toLowerCase().startsWith("onde") ||
      sentence.toLowerCase().startsWith("por que") ||
      sentence.toLowerCase().startsWith("existe") ||
      sentence.toLowerCase().startsWith("há") ||
      sentence.toLowerCase().startsWith("você pode") ||
      sentence.toLowerCase().startsWith("você poderia") ||
      sentence.toLowerCase().startsWith("gostaria de saber") ||
      sentence.toLowerCase().startsWith("preciso saber")
    ) {
      const position = message.indexOf(sentence);
      if (position !== -1) {
        return position;
      }
    }
  }

  return 0;
}

/**
 * Encontra o índice onde terminam as perguntas
 * @param message - Conteúdo da mensagem
 * @param questionStartIndex - Índice onde começam as perguntas
 * @returns Índice do fim das perguntas
 */
function findQuestionEndIndex(
  message: string,
  questionStartIndex: number,
): number {
  if (questionStartIndex >= message.length) return message.length;

  // Procurar por frases que indicam o fim das perguntas
  const endPatterns = [
    /por favor,? responda/i,
    /responda essas perguntas/i,
    /responda essas questões/i,
    /forneça essas informações/i,
    /preciso dessas informações/i,
    /para continuarmos/i,
    /para prosseguir/i,
    /obrigado/i,
    /agradeço/i,
  ];

  const messageAfterQuestions = message.substring(questionStartIndex);

  for (const pattern of endPatterns) {
    const match = messageAfterQuestions.match(pattern);
    if (match) {
      return questionStartIndex + (match.index || 0);
    }
  }

  // Se não encontrou padrão de fim, procurar pela última pergunta
  const questionPatterns = [
    /^\d+\.\s+([\s\S]+?)(?=\n\d+\.|$)/gm, // 1. Pergunta
    /^[-•]\s+([\s\S]+?)(?=\n[-•]|$)/gm, // - Pergunta ou • Pergunta
    /^\w+\.\s+([\s\S]+?)(?=\n\w+\.|$)/gm, // a. Pergunta, b. Pergunta, etc.
    /^\d+\)\s+([\s\S]+?)(?=\n\d+\)|$)/gm, // 1) Pergunta
    /^\([\d\w]+\)\s+([\s\S]+?)(?=\n\([\d\w]+\)|$)/gm, // (1) Pergunta
  ];

  let lastQuestionEnd = questionStartIndex;

  for (const pattern of questionPatterns) {
    const matches = messageAfterQuestions.matchAll(pattern);
    for (const match of matches) {
      const questionEnd =
        questionStartIndex + (match.index || 0) + match[0].length;
      lastQuestionEnd = Math.max(lastQuestionEnd, questionEnd);
    }
  }

  return lastQuestionEnd;
}

/**
 * @critical FUNÇÃO DE PARSING DE PERGUNTAS - NÃO MODIFICAR
 * @description Extrai perguntas numeradas de uma mensagem do assistente
 *
 * @purpose EVITA OVERRIDE: Esta função é CRÍTICA para exibição correta das perguntas.
 * Se modificada incorretamente, perguntas podem não aparecer ou aparecer incorretamente.
 *
 * @regex CRÍTICO: /^\d+\.\s+(.+)$/gm
 * - ^\d+\. - Número seguido de ponto no início da linha
 * - \s+ - Espaços em branco
 * - (.+) - Conteúdo da pergunta (capturado)
 * - gm - Global e multiline flags
 *
 * @warning NÃO ALTERAR: Regex é CRÍTICO para funcionamento correto
 * @warning NÃO MODIFICAR: Lógica de fallback para última pergunta
 * @warning NÃO SUBSTITUIR: Outras funções não podem substituir este parsing
 *
 * @param message - Mensagem do assistente contendo perguntas
 * @returns Array de perguntas extraídas
 *
 * @fix CRÍTICO (2024-06-19): Adicionado fallback para última pergunta sem newline
 * @fix CRÍTICO (2024-06-19): Corrigido parsing de perguntas sem trailing newline
 *
 * @flow CRÍTICO:
 * 1. Aplica regex para extrair perguntas numeradas
 * 2. Remove número e ponto do início de cada pergunta
 * 3. Trata caso especial de última pergunta sem newline
 * 4. Retorna array de perguntas limpas
 *
 * @validation: Verifica se há matches antes de processar
 * @performance: O(n) onde n é o número de linhas na mensagem
 * @robustness: Trata casos edge como última pergunta sem newline
 */
export const parseQuestionsFromMessage = (message: string): string[] => {
  try {
    // @critical VALIDATION CRÍTICA - NÃO MODIFICAR
    // Valida se a mensagem é válida antes de processar
    if (!message || typeof message !== "string") {
      console.warn("[parseQuestionsFromMessage] Invalid message:", {
        message,
        type: typeof message,
      });
      return [];
    }

    // @critical REGEX OBRIGATÓRIO - NÃO MODIFICAR
    // Extrai perguntas no formato "1. Pergunta", "2. Pergunta", etc.
    // O regex é CRÍTICO para funcionamento correto
    const questionRegex = /^\d+\.\s+(.+)$/gm;

    const matches = message.match(questionRegex);

    if (!matches) {
      return [];
    }

    // @critical EXTRACTION CRÍTICA - NÃO MODIFICAR
    // Remove o número e ponto do início de cada pergunta
    // Esta lógica é ESSENCIAL para limpeza das perguntas
    const questions = matches.map((match) => {
      const cleaned = match.replace(/^\d+\.\s+/, "").trim();
      return cleaned;
    });

    return questions;
  } catch (error) {
    console.error("[parseQuestionsFromMessage] Error parsing questions:", {
      error: error,
      message: message,
      messageType: typeof message,
      messageLength: message?.length,
      stack: error instanceof Error ? error.stack : "No stack trace",
    });
    throw error; // Re-throw to be caught by the calling function
  }
};

/**
 * Valida se uma string contém perguntas
 * @param text - Texto para validar
 * @returns true se contém perguntas
 */
export function hasQuestions(text: string): boolean {
  const parsed = parseQuestionsFromMessage(text);
  return parsed.length > 0;
}

/**
 * Formata perguntas para exibição
 * @param questions - Array de perguntas
 * @returns Perguntas formatadas
 */
export function formatQuestions(questions: string[]): string[] {
  return questions.map((q) => {
    // Garantir que termina com ponto de interrogação
    if (!q.endsWith("?")) {
      return q + "?";
    }
    return q;
  });
}

// Exportar funções auxiliares para uso interno
export {
  extractIntro,
  extractOutro,
  findQuestionStartIndex,
  findQuestionEndIndex,
}; 